import { useState } from 'react';

import { ApiError, api, type WebhookDelivery } from '../lib/api';
import { Alert, Badge, Card, Field, FieldGroup, PageHead, SecretReveal, formatTime, useLoader } from '../components/ui';

const EVENT_TYPES = [
  'delivery',
  'bounce',
  'complaint',
  'open',
  'click',
  'send',
  'reject',
  'delivery_delay',
  'rendering_failure',
];

/** null next_retry_at with null delivered_at and attempts spent means it is done. */
function deliveryState(delivery: WebhookDelivery): { kind: string; label: string } {
  if (delivery.delivered_at) return { kind: 'ok', label: 'delivered' };
  if (delivery.next_retry_at) return { kind: 'warn', label: 'retrying' };
  return { kind: 'danger', label: 'failed' };
}

export function Webhooks() {
  const { data, error, loading, reload } = useLoader(() => api.webhooks.list());
  const [creating, setCreating] = useState(false);
  const [targetUrl, setTargetUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['delivery', 'bounce', 'complaint']);
  const [secret, setSecret] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // Separate from formError: the API's `insecure_target_url` is about one
  // field, and an error shown against the field it concerns is actionable in a
  // way a banner at the top of the form is not.
  const [urlError, setUrlError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const deliveries = useLoader(
    () => (openId ? api.webhooks.deliveries(openId) : Promise.resolve([])),
    [openId],
  );

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);
    setUrlError(null);

    // Checked here as well as on the server. Not instead of: the server's check
    // is the one that counts, and this one exists so the answer arrives without a
    // round trip and lands on the field rather than above the form.
    if (!/^https:\/\/.+/i.test(targetUrl.trim())) {
      setUrlError('Enter an https:// URL — payloads carry recipient addresses.');
      return;
    }

    setBusy(true);
    try {
      const webhook = await api.webhooks.create({
        target_url: targetUrl.trim(),
        subscribed_events: events,
      });
      setSecret(webhook.signing_secret ?? null);
      setCreating(false);
      setTargetUrl('');
      reload();
    } catch (err) {
      // A rejection the server attributes to one field is shown on that field;
      // anything else is a form-level problem.
      if (err instanceof ApiError && err.code === 'insecure_target_url') {
        setUrlError(err.message);
      } else {
        setFormError(err instanceof Error ? err.message : 'Could not create webhook');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        title="Webhooks"
        subtitle="Signed event delivery, with the full attempt history for debugging an endpoint that is failing."
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreating(!creating)}
          >
            {creating ? 'Cancel' : 'Add endpoint'}
          </button>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}
      {notice && <Alert kind="info">{notice}</Alert>}

      {secret && (
        <Card title="Signing secret">
          <SecretReveal label="Secret" value={secret} />
          <p className="subtitle">
            Verify every delivery against the raw request body using this secret. The
            signature covers a timestamp as well as the body, so a captured request cannot
            be replayed at your endpoint.
          </p>
          <button type="button" className="btn btn-sm" onClick={() => setSecret(null)}>
            Done
          </button>
        </Card>
      )}

      {creating && (
        <Card title="Add a webhook endpoint">
          {formError && <Alert kind="error">{formError}</Alert>}
          <form onSubmit={create}>
            <Field
              id="target"
              label="Endpoint URL"
              error={urlError}
              hint="Must be https — payloads carry recipient addresses."
            >
              {(props) => (
                <input
                  {...props}
                  required
                  placeholder="https://yourapp.com/hooks/mailgiri"
                  value={targetUrl}
                  onChange={(event) => {
                    setTargetUrl(event.target.value);
                    // Cleared as soon as the value changes, so a corrected field
                    // stops showing a stale error.
                    if (urlError) setUrlError(null);
                  }}
                />
              )}
            </Field>

            <FieldGroup id="hook-events" label="Events">
              <div className="checkbox-grid">
                {EVENT_TYPES.map((eventType) => (
                  <label className="checkbox-row" key={eventType}>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={events.includes(eventType)}
                      onChange={() =>
                        setEvents((current) =>
                          current.includes(eventType)
                            ? current.filter((item) => item !== eventType)
                            : [...current, eventType],
                        )
                      }
                    />
                    <span className="mono">{eventType}</span>
                  </label>
                ))}
              </div>
            </FieldGroup>

            <button className="btn btn-primary" disabled={busy || events.length === 0}>
              {busy ? 'Creating…' : 'Create endpoint'}
            </button>
          </form>
        </Card>
      )}

      {(data ?? []).map((webhook) => (
        <Card
          key={webhook.id}
          title={webhook.target_url}
          actions={
            <div className="row">
              <Badge kind={webhook.is_active ? 'ok' : 'muted'}>
                {webhook.is_active ? 'active' : 'paused'}
              </Badge>
              <button
                type="button"
                className="btn btn-sm"
                onClick={async () => {
                  const result = await api.webhooks.sendTest(webhook.id);
                  setNotice(
                    result.delivered_at
                      ? `Test event delivered (HTTP ${result.last_status_code}).`
                      : `Test event was not accepted (HTTP ${result.last_status_code ?? 'no response'}).`,
                  );
                  if (openId === webhook.id) deliveries.reload();
                }}
              >
                Send test
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setOpenId(openId === webhook.id ? null : webhook.id)}
              >
                {openId === webhook.id ? 'Hide history' : 'History'}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={async () => {
                  await api.webhooks.update(webhook.id, { is_active: !webhook.is_active });
                  reload();
                }}
              >
                {webhook.is_active ? 'Pause' : 'Resume'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={async () => {
                  if (!window.confirm('Delete this endpoint?')) return;
                  await api.webhooks.remove(webhook.id);
                  if (openId === webhook.id) setOpenId(null);
                  reload();
                }}
              >
                Delete
              </button>
            </div>
          }
        >
          <div className="row" style={{ gap: 5 }}>
            {webhook.subscribed_events.map((eventType) => (
              <Badge kind="muted" key={eventType}>
                {eventType}
              </Badge>
            ))}
          </div>

          {openId === webhook.id && (
            <div style={{ marginTop: 16 }}>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>State</th>
                      <th className="num">Attempts</th>
                      <th className="num">HTTP</th>
                      <th>Last error</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(deliveries.data ?? []).map((delivery) => {
                      const state = deliveryState(delivery);
                      return (
                        <tr key={delivery.id}>
                          <td className="mono">{delivery.event_type}</td>
                          <td>
                            <Badge kind={state.kind}>{state.label}</Badge>
                          </td>
                          <td className="num">{delivery.attempt_count}</td>
                          <td className="num">{delivery.last_status_code ?? '—'}</td>
                          <td className="truncate" title={delivery.last_error ?? ''}>
                            {delivery.last_error ?? '—'}
                          </td>
                          <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                            {formatTime(delivery.created_at)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {!delivery.delivered_at && (
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={async () => {
                                  await api.webhooks.retry(webhook.id, delivery.id);
                                  deliveries.reload();
                                }}
                              >
                                Retry
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {(deliveries.data ?? []).length === 0 && (
                <div className="empty">
                  {deliveries.loading ? 'Loading…' : 'No deliveries yet.'}
                </div>
              )}
              <p className="hint" style={{ marginTop: 10 }}>
                Failed attempts back off 1min → 5min → 30min → 2h, then stop. Retrying
                resets the schedule, since you have usually just fixed the endpoint.
              </p>
            </div>
          )}
        </Card>
      ))}

      {(data ?? []).length === 0 && !creating && (
        <Card>
          <div className="empty">
            {loading ? 'Loading…' : 'No webhook endpoints yet.'}
          </div>
        </Card>
      )}
    </>
  );
}
